import type { AxiosInstance } from "axios";
import { Loader2, Plus, Rocket } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { Select } from "../components/ui/select.js";
import { apiErrorMessage } from "../lib/api.js";
import {
  createPlan,
  fetchPlans,
  planLimit,
  publishPlan,
  REPORT_CATALOG,
  setPlanReports,
  updatePlan,
  type PlanSummary,
} from "../lib/admin.js";

/**
 * The plan catalogue and builder.
 *
 * A plan bundles three things a merchant then lives inside: which REPORTS they can open,
 * and the LIMITS on seats (people they can invite) and stores. All three are edited here;
 * a priced plan can also be published to Razorpay so the price shown and the price charged
 * are entered once, not twice.
 */
const LIMIT_KEYS: readonly { readonly key: string; readonly label: string; readonly hint: string }[] = [
  { key: "seats", label: "Seats (users)", hint: "How many people this org can invite" },
  { key: "max_stores", label: "Stores", hint: "How many stores this org can connect" },
  { key: "max_clients", label: "Clients", hint: "Agency workspaces (agency plans)" },
];

export function PlansScreen({ client }: { readonly client: AxiosInstance }): React.JSX.Element {
  const [plans, setPlans] = useState<readonly PlanSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setPlans(await fetchPlans(client));
      setError(null);
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not load plans."));
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="flex w-full flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Plans</h1>
          <p className="text-muted-foreground text-sm">What each plan includes, its limits, and whether it is sellable.</p>
        </div>
        <Button type="button" onClick={() => setCreating((c) => !c)}>
          <Plus className="size-4" />
          New plan
        </Button>
      </header>

      {error !== null && <p className="text-destructive text-sm">{error}</p>}

      {creating && (
        <NewPlanForm
          client={client}
          onCreated={() => {
            setCreating(false);
            void load();
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      {plans === null ? (
        <p className="text-muted-foreground flex items-center gap-2 p-6 text-sm">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </p>
      ) : (
        plans.map((plan) => <PlanCard key={plan.code} plan={plan} client={client} onChanged={() => void load()} />)
      )}
    </div>
  );
}

function ReportChecklist({
  selected,
  onToggle,
}: {
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (key: string) => void;
}): React.JSX.Element {
  return (
    <div className="grid gap-1.5 sm:grid-cols-2">
      {REPORT_CATALOG.map((report) => (
        <label key={report.key} className="flex items-center gap-2 text-sm">
          <input type="checkbox" className="size-4" checked={selected.has(report.key)} onChange={() => onToggle(report.key)} />
          <span>{report.name}</span>
        </label>
      ))}
    </div>
  );
}

function NewPlanForm({
  client,
  onCreated,
  onCancel,
}: {
  readonly client: AxiosInstance;
  readonly onCreated: () => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"brand" | "agency">("brand");
  const [priceRupees, setPriceRupees] = useState("0");
  const [interval, setInterval] = useState<"monthly" | "yearly">("monthly");
  const [trialDays, setTrialDays] = useState("0");
  const [limits, setLimits] = useState<Record<string, string>>({ seats: "", max_stores: "", max_clients: "" });
  const [reports, setReports] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(key: string): void {
    setReports((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const limitObj: Record<string, number | null> = {};
      for (const { key } of LIMIT_KEYS) {
        const raw = limits[key]?.trim() ?? "";
        if (raw !== "") limitObj[key] = Number(raw);
      }
      await createPlan(client, {
        code: code.trim(),
        name: name.trim(),
        kind,
        priceMinor: Math.round(Number(priceRupees) * 100),
        interval,
        trialDays: Number(trialDays),
        limits: limitObj,
        reportKeys: [...reports],
      });
      onCreated();
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not create the plan."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="border-primary/40">
      <form onSubmit={(e) => void submit(e)}>
        <CardHeader>
          <CardTitle className="text-base">New plan</CardTitle>
          <CardDescription>Attach reports and limits now; you can change them any time.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Code" hint="Stable id, e.g. growth">
              <Input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="growth" />
            </Field>
            <Field label="Name">
              <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Growth" />
            </Field>
            <Field label="Type">
              <Select value={kind} onChange={(e) => setKind(e.target.value as "brand" | "agency")}>
                <option value="brand">Brand</option>
                <option value="agency">Agency</option>
              </Select>
            </Field>
            <Field label="Billing">
              <Select value={interval} onChange={(e) => setInterval(e.target.value as "monthly" | "yearly")}>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </Select>
            </Field>
            <Field label="Price" hint="0 = free">
              <Input type="number" min="0" step="0.01" value={priceRupees} onChange={(e) => setPriceRupees(e.target.value)} />
            </Field>
            <Field label="Trial days">
              <Input type="number" min="0" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} />
            </Field>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium">Limits</p>
            <div className="grid gap-3 sm:grid-cols-3">
              {LIMIT_KEYS.map((l) => (
                <Field key={l.key} label={l.label} hint={`${l.hint} · blank = unlimited`}>
                  <Input
                    type="number"
                    min="0"
                    placeholder="∞"
                    value={limits[l.key] ?? ""}
                    onChange={(e) => setLimits((cur) => ({ ...cur, [l.key]: e.target.value }))}
                  />
                </Field>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium">Included reports</p>
            <ReportChecklist selected={reports} onToggle={toggle} />
          </div>

          {error !== null && (
            <p className="text-destructive text-sm" role="alert">
              {error}
            </p>
          )}

          <div className="flex items-center gap-2">
            <Button type="submit" disabled={busy}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
              Create plan
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </form>
    </Card>
  );
}

function Field({ label, hint, children }: { readonly label: string; readonly hint?: string; readonly children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      {children}
      {hint !== undefined && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

function PlanCard({
  plan,
  client,
  onChanged,
}: {
  readonly plan: PlanSummary;
  readonly client: AxiosInstance;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set(plan.reportKeys));
  const [limits, setLimits] = useState<Record<string, string>>({
    seats: planLimit(plan.limits, "seats"),
    max_stores: planLimit(plan.limits, "max_stores"),
    max_clients: planLimit(plan.limits, "max_clients"),
  });
  const [busy, setBusy] = useState<null | "reports" | "limits" | "publish">(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const free = plan.priceMinor === 0;
  const reportsDirty = selected.size !== plan.reportKeys.length || plan.reportKeys.some((k) => !selected.has(k));

  function toggle(key: string): void {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function run(which: "reports" | "limits" | "publish", fn: () => Promise<void>, ok: string): Promise<void> {
    setBusy(which);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(ok);
      onChanged();
    } catch (caught) {
      setError(apiErrorMessage(caught, "Something went wrong."));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          {plan.name}
          <Badge variant="outline" className="capitalize">
            {plan.kind}
          </Badge>
          {plan.visibility !== "public" && <Badge variant="secondary">{plan.visibility}</Badge>}
          {!plan.isActive && <Badge className="border-transparent bg-slate-500/15 text-slate-600">inactive</Badge>}
        </CardTitle>
        <CardDescription>
          {free ? "Free" : `${(plan.priceMinor / 100).toLocaleString()} ${plan.currency} / ${plan.interval}`}
          {plan.trialDays > 0 ? ` · ${String(plan.trialDays)}-day trial` : ""} · {plan.subscriberCount} subscribers ·{" "}
          <span className="font-mono text-xs">{plan.code}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <p className="mb-2 text-sm font-medium">Included reports</p>
            <ReportChecklist selected={selected} onToggle={toggle} />
            <Button
              type="button"
              size="sm"
              className="mt-3"
              disabled={busy !== null || !reportsDirty}
              onClick={() => void run("reports", () => setPlanReports(client, plan.code, [...selected]).then(() => undefined), "Reports saved.")}
            >
              {busy === "reports" ? <Loader2 className="size-4 animate-spin" /> : null}
              Save reports
            </Button>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium">Limits</p>
            <div className="grid gap-3 sm:grid-cols-3">
              {LIMIT_KEYS.map((l) => (
                <div key={l.key} className="grid gap-1.5">
                  <Label className="text-xs">{l.label}</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="∞"
                    value={limits[l.key] ?? ""}
                    onChange={(e) => setLimits((cur) => ({ ...cur, [l.key]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-3"
              disabled={busy !== null}
              onClick={() =>
                void run(
                  "limits",
                  () => {
                    const limitObj: Record<string, number | null> = {};
                    for (const { key } of LIMIT_KEYS) {
                      const raw = limits[key]?.trim() ?? "";
                      limitObj[key] = raw === "" ? null : Number(raw);
                    }
                    return updatePlan(client, plan.code, { limits: limitObj }).then(() => undefined);
                  },
                  "Limits saved — every org on this plan re-evaluated.",
                )
              }
            >
              {busy === "limits" ? <Loader2 className="size-4 animate-spin" /> : null}
              Save limits
            </Button>
          </div>
        </div>

        {error !== null && (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        )}
        {notice !== null && (
          <p className="text-sm text-emerald-700 dark:text-emerald-400" role="status">
            {notice}
          </p>
        )}

        {!free ? (
          <Button
            type="button"
            variant="outline"
            className="w-fit"
            disabled={busy !== null}
            onClick={() => void run("publish", () => publishPlan(client, plan.code).then(() => undefined), "Published to Razorpay — its charge schedule is live.")}
          >
            {busy === "publish" ? <Loader2 className="size-4 animate-spin" /> : <Rocket className="size-4" />}
            Publish to Razorpay
          </Button>
        ) : (
          <span className="text-muted-foreground text-xs">Free plans need no Razorpay schedule.</span>
        )}
      </CardContent>
    </Card>
  );
}
